import React, { memo, useCallback, useEffect, useState } from 'react';

import { Link } from 'react-router-dom';
import { Glyphicon, Checkbox } from 'react-bootstrap';
import { connect, useSelector } from 'react-redux';
import styled from 'styled-components';

import PhotoWidget from '../utils/FormWidgets/PhotoWidget';
import {
  createSlug,
  isMobileResolution,
} from '../../infrastructure/utils/common';
import NoImage from '../../NoImage.png';
import { City, CityGuide, CityPicture } from '../../types/City';
import { getItineraryTemplates } from '../../services/templates';
import {
  fetchCityTemplateAction,
  selectAreCityItineraryTemplatesLoading,
  selectCityItineraryTemplates,
  selectHaveCityItineraryTemplatesLoaded,
  setCityTemplateAction,
} from '../../reduces/itineraryTemplates';
import { useAppDispatch } from '../../store';
import { RootState } from '../../reduces/reducer';

import { CityActions } from './Cities/Cities';

type Props = {
  actions: CityActions;
  value: City;
  ind: string;
  deletedGuide: string;
  guides: CityGuide[];
  pr: string;
};

const Tr = styled.tr`
  &&& {
    td {
      text-align: center;
      vertical-align: middle;
      cursor: pointer;
    }
  }
`;

const CityElement = memo(
  ({ actions, value, ind, deletedGuide, guides = [], pr }: Props) => {
    const {
      exploreConfigurations = [],
      name,
      status,
      top,
      picture: initialPicture,
    } = value;
    const slug = createSlug(name);
    const dispatch = useAppDispatch();
    const itineraryTemplates = useSelector((state: RootState) =>
      selectCityItineraryTemplates(state, slug)
    );
    const isTemplateLoading = useSelector((state: RootState) =>
      selectAreCityItineraryTemplatesLoading(state, slug)
    );
    const hasTemplateLoaded = useSelector((state: RootState) =>
      selectHaveCityItineraryTemplatesLoaded(state, slug)
    );
    const explore = exploreConfigurations.length > 0;
    const exploreAction = explore ? 'Edit' : 'Create';
    const exploreBtnType = exploreAction === 'Edit' ? 'def' : 'primary';
    const guide = guides.find(
      ({ city, id }) => city === name && id !== deletedGuide
    );
    const guideAction = guide ? 'Edit' : 'Create';
    const { published } = guide ?? {};
    const cityStatus = published ? 'Published' : 'Draft';
    const guideBtnType = guideAction === 'Edit' ? 'def' : 'primary';
    const [picture, setPictire] = useState<CityPicture>(initialPicture);

    const handleOnStatusClick = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      event.preventDefault();
      actions.handleOnStatusClick(name);
    };

    const handleOnTopClick = (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      actions.handleOnTopClick(name);
    };

    const handleOnExploreClick = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      event.preventDefault();
      actions.handleOnExploreClick((event as unknown) as KeyboardEvent, name);
    };

    const handleOnGuideClick = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      event.preventDefault();
      actions.handleOnGuideClick((event as unknown) as KeyboardEvent, name);
    };

    const uploadPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      const { target } = event;
      actions.uploadPhoto(target, name);
    };

    const removeCity = event => {
      event.preventDefault();
      actions.deleteCity(name);
    };
    const fetchCityTemplates = useCallback(async () => {
      if (!isTemplateLoading && !hasTemplateLoaded) {
        try {
          dispatch(fetchCityTemplateAction(slug));
          const template = await getItineraryTemplates(slug);
          dispatch(setCityTemplateAction(slug, template));
        } catch (error) {}
      }
    }, [dispatch, hasTemplateLoaded, isTemplateLoading, slug]);

    useEffect(() => {
      fetchCityTemplates();
    }, [fetchCityTemplates]);

    return (
      <>
        <Tr key={value.name} data-name={ind} className='listrow moveable'>
          <td>
            <Glyphicon glyph='option-vertical' />
          </td>
          <td>
            <PhotoWidget
              image={picture}
              loadkey={pr}
              onChange={uploadPhoto}
              onError={() => {
                setPictire({ isNew: true, blob: null, data: NoImage });
              }}
              readOnly={isMobileResolution()}
              header={''}
            />
          </td>
          <td>{name}</td>
          <td>
            <Checkbox
              val='active'
              style={{ marginBottom: '4px' }}
              onClick={handleOnStatusClick}
              checked={status === 'active'}
            />
          </td>
          <td>
            {isTemplateLoading ? (
              <Glyphicon glyph='refresh' className='spinning' />
            ) : (
              <Link to={`/cities/${slug}/itinerary-templates`}>
                <button
                  type='button'
                  style={{ width: '120px' }}
                  className={`btn btn-${
                    itineraryTemplates.length ? 'primary' : 'def'
                  }`}
                >
                  {itineraryTemplates.length ? 'Edit' : 'Create'}
                </button>
              </Link>
            )}
          </td>
          <td>
            <Checkbox
              val='active'
              style={{ marginBottom: '4px' }}
              onClick={handleOnTopClick}
              checked={top}
            />
          </td>
          <td>
            <button
              type='button'
              style={{ width: '120px' }}
              onClick={handleOnExploreClick}
              className={`btn btn-${exploreBtnType}`}
            >{`${exploreAction}`}</button>
          </td>
          <td>
            <button
              type='button'
              style={{ width: '120px' }}
              onClick={handleOnGuideClick}
              className={`btn btn-${guideBtnType}`}
            >{`${guideAction}`}</button>
          </td>
          <td>{cityStatus}</td>
          <td>
            <i className='fas fa-times' onClick={removeCity} />
          </td>
        </Tr>
      </>
    );
  }
);

function mapStateToProps(state) {
  return {
    deletedGuide: state.cityGuides.deletedGuide,
  };
}

export default connect(mapStateToProps)(CityElement);
